import { Capacitor } from '@capacitor/core'
import { useMemo } from 'react'
import { useRecoilValue } from 'recoil'
import { useTheme } from 'styled-components'

import { environment } from '@/config/environment'
import { useLayout } from '@/layout/providers/LayoutProvider'
import { useAppInfo } from '@/modules/shared/hooks/useAppInfo'
import { mutedBannerColor } from '@/utils/color'
import { DEV_APP_ID, STAGING_APP_ID } from '@/utils/environment'
import { modeAtom } from '@/utils/qa-mode'

/**
 * Mirrors MandateBanner's background. No theme token carries this tint;
 * theme-generated.ts is synced from ZeroHeight, so it gains one there rather
 * than by hand, and until it does both places repeat the hex.
 */
const MANDATE_BANNER_COLOR = '#e6ebf2'

type UseSafeAreaColorOptions = {
  isLoggedInForSomeoneElse: boolean
}

/**
 * Returns the appropriate background color for the safe area top region.
 * The color matches the first visible banner:
 * - EnvironmentBanner color (red/orange/green) if visible
 * - MandateBanner color (#e6ebf2) if logged in for someone else
 * - ContentPageLayout background color if set
 * - The light background color otherwise
 *
 * Banner colors are muted to match the banner's own 0.7 translucency.
 */
export function useSafeAreaColor({ isLoggedInForSomeoneElse }: UseSafeAreaColorOptions): string {
  const theme = useTheme()
  const info = useAppInfo()
  const appId = info?.id
  const isQaMode = useRecoilValue(modeAtom) === 'qa'
  const { contentBackgroundColor } = useLayout()

  return useMemo(() => {
    const muted = (color: string) => mutedBannerColor(color, theme.Colors.Background.Light)

    // Check if environment banner would be visible
    const isProduction = environment.env === 'production'
    const isContentPreview = window.location.pathname.includes('content-preview')
    const showEnvironmentBanner = (!isProduction || isQaMode) && !isContentPreview && info && appId

    if (showEnvironmentBanner) {
      if (Capacitor.isNativePlatform()) {
        if (appId === DEV_APP_ID) {
          return muted(theme.Colors.Status.Error)
        }
        if (appId === STAGING_APP_ID) {
          return muted(theme.Colors.Status.Warning)
        }
      } else {
        if (environment.env === 'development') {
          return muted(theme.Colors.Status.Error)
        }
        if (environment.env === 'staging') {
          return muted(theme.Colors.Status.Warning)
        }
      }
      if (isQaMode) {
        return muted(theme.Colors.Status.Success)
      }
    }

    // Check if mandate banner would be visible
    if (isLoggedInForSomeoneElse) {
      return MANDATE_BANNER_COLOR
    }

    return contentBackgroundColor ?? theme.Colors.Background.Light
  }, [theme, appId, isQaMode, info, isLoggedInForSomeoneElse, contentBackgroundColor])
}
